import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '../ui-kit/input';
import { Button } from '../ui-kit/button';
import { Label } from '../ui-kit/label';
import { supabaseClient, updateUserPassword } from '../services/supabase';
import { isValidPassword, PASSWORD_REQUIREMENTS_MESSAGE } from '../shared/passwordValidation';
import { clearPasswordRecoveryPending } from '../utils/passwordRecoveryPending';
import {
    establishRecoverySessionFromUrl,
    getAuthCallbackError,
    stripAuthParamsFromUrl,
} from '../utils/recoverySession';
import { needsMfaVerification, verifyMfaChallenge } from '../services/authSecurity';

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [sessionReady, setSessionReady] = useState(false);
    const [sessionError, setSessionError] = useState(() => getAuthCallbackError() ?? '');
    const [needsMfaStepUp, setNeedsMfaStepUp] = useState(false);
    const [mfaReady, setMfaReady] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(true);

    useEffect(() => {
        if (sessionError) {
            stripAuthParamsFromUrl();
            clearPasswordRecoveryPending();
            return;
        }

        let cancelled = false;
        void (async () => {
            const result = await establishRecoverySessionFromUrl();
            if (cancelled) return;
            if (!result.ok) {
                clearPasswordRecoveryPending();
                setSessionError(result.error ?? 'Unable to verify reset link.');
                return;
            }

            setSessionReady(true);
            try {
                // Recovery links mint AAL1. MFA users must step up before updateUser({ password }).
                const stepUpRequired = await needsMfaVerification();
                if (cancelled) return;
                setNeedsMfaStepUp(stepUpRequired);
                setMfaReady(!stepUpRequired);
            } catch (err) {
                if (cancelled) return;
                const message =
                    err instanceof Error ? err.message : 'Unable to check two-factor requirements.';
                setSessionError(message);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionError]);

    const handleMfaVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await verifyMfaChallenge(mfaCode.trim());
            setMfaReady(true);
            setNeedsMfaStepUp(false);
            setMfaCode('');
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Verification failed. Please try again.';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!isValidPassword(password)) {
            setError(PASSWORD_REQUIREMENTS_MESSAGE);
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            // Re-check in case MFA was required and session is still AAL1.
            if (await needsMfaVerification()) {
                setNeedsMfaStepUp(true);
                setMfaReady(false);
                setError('Enter your authenticator code before setting a new password.');
                return;
            }

            await updateUserPassword(password);
            clearPasswordRecoveryPending();
            await supabaseClient.auth.signOut();
            navigate('/login', {
                state: { message: 'Your password has been updated. Sign in with your new password.' },
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update password.';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ResetPasswordLayout>
            {sessionError ? (
                <>
                    <h2 className="text-2xl font-semibold text-foreground">Reset link expired</h2>
                    <p className="mt-2 text-sm text-destructive">{sessionError}</p>
                    <Link to="/login" className="mt-6 inline-block text-sm text-primary hover:text-primary/80">
                        Back to sign in
                    </Link>
                </>
            ) : !sessionReady ? (
                <p className="text-muted-foreground">Verifying your reset link…</p>
            ) : needsMfaStepUp && !mfaReady ? (
                <>
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-foreground">Confirm it&apos;s you</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            This account uses two-factor authentication. Enter your authenticator code
                            to continue resetting your password.
                        </p>
                    </div>
                    <form onSubmit={(e) => void handleMfaVerify(e)} className="mt-8 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="reset-mfa-code">Authentication code</Label>
                            <Input
                                id="reset-mfa-code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="123456"
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value.trim())}
                                autoFocus
                                required
                            />
                        </div>
                        {error && <p className="text-destructive text-sm">{error}</p>}
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isLoading || mfaCode.length < 6}
                        >
                            {isLoading ? 'Verifying…' : 'Verify and continue'}
                        </Button>
                    </form>
                    <Link
                        to="/signout"
                        className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground"
                    >
                        Cancel and sign out
                    </Link>
                </>
            ) : (
                <>
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-foreground">Set a new password</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Choose a strong password for your account
                        </p>
                    </div>
                    <form onSubmit={(e) => void handleSubmit(e)} className="mt-8 space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">New password</label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setIsPasswordValid(isValidPassword(e.target.value));
                                }}
                                required
                                autoComplete="new-password"
                                className={!isPasswordValid && password ? 'border-destructive' : ''}
                            />
                            {password && !isPasswordValid && (
                                <p className="text-sm text-muted-foreground">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Confirm new password</label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                            />
                        </div>
                        {error && <p className="text-destructive text-sm">{error}</p>}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full py-2 px-4 rounded-md text-primary-foreground transition-colors ${
                                isLoading ? 'bg-muted cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                            }`}
                        >
                            {isLoading ? 'Updating…' : 'Update password'}
                        </button>
                    </form>
                    <Link
                        to="/signout"
                        className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground"
                    >
                        Cancel and sign out
                    </Link>
                </>
            )}
        </ResetPasswordLayout>
    );
};

function ResetPasswordLayout({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-full min-h-0 items-center justify-center p-8">
            <div className="w-full max-w-md space-y-8 rounded-2xl bg-card p-8 shadow-lg">{children}</div>
        </div>
    );
}

export default ResetPasswordPage;
